import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import Axios from '../config/axios';
import { UrlConstant } from '../commom/UrlConstant';
import { endLoading, startLoading } from '../Redux/loadingSlice';

export type PaymentMethod = {
    id: string;
    type: 'bankAccount' | 'wallet' | 'shareSellDown';
    displayName: string;
    displayNumber: string;
    accountType?: string;
    bankName?: string;
    accountNickname?: string;
    isSelected?: boolean;
    status?: string;
    createdDate?: Date;
    metadata?: Record<string, any>;
};

export const usePaymentMethods = () => {
    const dispatch = useDispatch();

    const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethod | null>(null);
    const [defaultMethod, setDefaultMethod] = useState<PaymentMethod | null>(null);

    /**
     * Fetch all payment methods from the new unified API
     */
    const fetchAllPaymentMethods = useCallback(async () => {
        dispatch(startLoading());

        try {
            const res = await Axios.get(UrlConstant.User.GetAllPaymentMethods);

            const data = res?.data?.data;
            const methods: PaymentMethod[] = data?.paymentMethods ?? [];
            const defaultPaymentMethod: PaymentMethod | null = data?.defaultMethod ?? null;

            setPaymentMethods(methods);
            setDefaultMethod(defaultPaymentMethod);

            // Set selected payment method (priority: default from API > wallet > first available)
            const initialSelection =
                defaultPaymentMethod ||
                methods.find((m) => m.type === 'wallet') ||
                methods[0] ||
                null;

            setSelectedPaymentMethod((prev) => prev || initialSelection);

            return methods;
        } catch (error: any) {
            console.error('Error fetching payment methods:', error?.response || error);
            setPaymentMethods([]);
            setSelectedPaymentMethod(null);
            return [];
        } finally {
            dispatch(endLoading());
        }
    }, [dispatch]);

    const fetchPaymentMethods = useCallback(async () => {
        return fetchAllPaymentMethods();
    }, [fetchAllPaymentMethods]);

    const getMethodsByType = useCallback((type: PaymentMethod['type']) => {
        return paymentMethods.filter(method => method.type === type);
    }, [paymentMethods]);

    const getBankAccounts = useCallback(() => {
        return getMethodsByType('bankAccount');
    }, [getMethodsByType]);

    const hasPaymentType = useCallback((type: PaymentMethod['type']) => {
        return paymentMethods.some(method => method.type === type);
    }, [paymentMethods]);

    useEffect(() => {
        fetchPaymentMethods();
    }, [fetchPaymentMethods]);

    return {
        paymentMethods,
        selectedPaymentMethod,
        defaultMethod,
        setSelectedPaymentMethod,
        fetchPaymentMethods,
        fetchAllPaymentMethods,
        setPaymentMethods,
        getMethodsByType,
        getBankAccounts,
        hasPaymentType,
    } as const;
};

export default usePaymentMethods;